import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';

const surface = {
    0: '#ffffff',
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#707070',
    600: '#515c6f',
    700: '#434343',
    800: '#262626',
    900: '#171717',
    950: '#0a0a0a',
};

const GiraBrasilTheme = definePreset(Aura, {
    primitive: {
        borderRadius: {
            none: '0',
            xs: '2px',
            sm: '4px',
            md: '6px',
            lg: '8px',
            xl: '12px',
        },
        giraPrimary: {
            50: '#eef2ff',
            100: '#dfe6ff',
            200: '#bdccff',
            300: '#8fa6f5',
            400: '#6179d8',
            500: '#3d55ad',
            600: '#263f8a',
            700: '#122868',
            800: '#10235a',
            900: '#0d1c48',
            950: '#080f28',
        },
        giraSecondary: {
            50: '#fff8e6',
            100: '#ffedb8',
            200: '#ffdd80',
            300: '#ffc747',
            400: '#ffb51f',
            500: '#ffae00',
            600: '#d98f00',
            700: '#a86800',
            800: '#7a4b00',
            900: '#523200',
            950: '#2e1b00',
        },
    },
    semantic: {
        transitionDuration: '0.2s',
        focusRing: {
            width: '1px',
            style: 'solid',
            color: '{primary.color}',
            offset: '2px',
            shadow: 'none',
        },
        disabledOpacity: '0.6',
        iconSize: '1rem',
        anchorGutter: '2px',
        primary: {
            50: '{giraPrimary.50}',
            100: '{giraPrimary.100}',
            200: '{giraPrimary.200}',
            300: '{giraPrimary.300}',
            400: '{giraPrimary.400}',
            500: '{giraPrimary.700}',
            600: '{giraPrimary.800}',
            700: '{giraPrimary.900}',
            800: '{giraPrimary.950}',
            900: '{giraPrimary.950}',
            950: '{giraPrimary.950}',
        },
        formField: {
            paddingX: '0.75rem',
            paddingY: '0.5rem',
            sm: {
                fontSize: '0.875rem',
                paddingX: '0.625rem',
                paddingY: '0.375rem',
            },
            lg: {
                fontSize: '1.125rem',
                paddingX: '0.875rem',
                paddingY: '0.625rem',
            },
            borderRadius: '{border.radius.md}',
            focusRing: {
                width: '0',
                style: 'none',
                color: 'transparent',
                offset: '0',
                shadow: 'none',
            },
            transitionDuration: '{transition.duration}',
        },
        list: {
            padding: '0.25rem 0.25rem',
            gap: '2px',
            header: {
                padding: '0.5rem 1rem 0.25rem 1rem',
            },
            option: {
                padding: '0.5rem 0.75rem',
                borderRadius: '{border.radius.sm}',
            },
            optionGroup: {
                padding: '0.5rem 0.75rem',
                fontWeight: '600',
            },
        },
        content: {
            borderRadius: '{border.radius.md}',
        },
        mask: {
            transitionDuration: '0.15s',
        },
        navigation: {
            list: {
                padding: '0.25rem 0.25rem',
                gap: '2px',
            },
            item: {
                padding: '0.5rem 0.75rem',
                borderRadius: '{border.radius.sm}',
                gap: '0.5rem',
            },
            submenuLabel: {
                padding: '0.5rem 0.75rem',
                fontWeight: '600',
            },
            submenuIcon: {
                size: '0.875rem',
            },
        },
        overlay: {
            select: {
                borderRadius: '{border.radius.md}',
                shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            },
            popover: {
                borderRadius: '{border.radius.md}',
                padding: '0.75rem',
                shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            },
            modal: {
                borderRadius: '{border.radius.xl}',
                padding: '1.25rem',
                shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            },
            navigation: {
                shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            },
        },
        colorScheme: {
            light: {
                surface,
                primary: {
                    color: '{giraPrimary.700}',
                    contrastColor: '#ffffff',
                    hoverColor: '{giraPrimary.800}',
                    activeColor: '{giraPrimary.900}',
                },
                highlight: {
                    background: '{giraPrimary.50}',
                    focusBackground: '{giraPrimary.100}',
                    color: '{giraPrimary.700}',
                    focusColor: '{giraPrimary.800}',
                },
                mask: {
                    background: 'rgba(0,0,0,0.4)',
                    color: '{surface.200}',
                },
                formField: {
                    background: '{surface.50}',
                    disabledBackground: '{surface.200}',
                    filledBackground: '{surface.50}',
                    filledHoverBackground: '{surface.50}',
                    filledFocusBackground: '{surface.50}',
                    borderColor: '{surface.300}',
                    hoverBorderColor: '{surface.400}',
                    focusBorderColor: '{giraPrimary.700}',
                    invalidBorderColor: '{red.400}',
                    color: '{surface.700}',
                    disabledColor: '{surface.500}',
                    placeholderColor: '{surface.500}',
                    invalidPlaceholderColor: '{red.600}',
                    floatLabelColor: '{surface.500}',
                    floatLabelFocusColor: '{giraPrimary.700}',
                    floatLabelActiveColor: '{surface.500}',
                    floatLabelInvalidColor: '{red.600}',
                    iconColor: '{surface.400}',
                    shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
                },
                text: {
                    color: '{giraPrimary.700}',
                    hoverColor: '{surface.800}',
                    mutedColor: '{surface.500}',
                    hoverMutedColor: '{surface.600}',
                },
                content: {
                    background: '{surface.100}',
                    hoverBackground: '{surface.100}',
                    borderColor: '{surface.200}',
                    color: '{text.color}',
                    hoverColor: '{text.hover.color}',
                },
                overlay: {
                    select: {
                        background: '{surface.50}',
                        borderColor: '{surface.200}',
                        color: '{text.color}',
                    },
                    popover: {
                        background: '{surface.50}',
                        borderColor: '{surface.200}',
                        color: '{text.color}',
                    },
                    modal: {
                        background: '{surface.50}',
                        borderColor: '{surface.200}',
                        color: '{text.color}',
                    },
                },
                list: {
                    option: {
                        focusBackground: '{surface.100}',
                        selectedBackground: '{highlight.background}',
                        selectedFocusBackground: '{highlight.focus.background}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        selectedColor: '{highlight.color}',
                        selectedFocusColor: '{highlight.focus.color}',
                        icon: {
                            color: '{surface.400}',
                            focusColor: '{surface.500}',
                        },
                    },
                    optionGroup: {
                        background: 'transparent',
                        color: '{text.muted.color}',
                    },
                },
                navigation: {
                    item: {
                        focusBackground: '{surface.100}',
                        activeBackground: '{surface.100}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        activeColor: '{text.hover.color}',
                        icon: {
                            color: '{surface.400}',
                            focusColor: '{surface.500}',
                            activeColor: '{surface.500}',
                        },
                    },
                    submenuLabel: {
                        background: 'transparent',
                        color: '{text.muted.color}',
                    },
                    submenuIcon: {
                        color: '{surface.400}',
                        focusColor: '{surface.500}',
                        activeColor: '{surface.500}',
                    },
                },
            },
            dark: {
                surface,
                primary: {
                    color: '{giraSecondary.400}',
                    contrastColor: '{surface.900}',
                    hoverColor: '{giraSecondary.300}',
                    activeColor: '{giraSecondary.200}',
                },
                highlight: {
                    background: 'color-mix(in srgb, {giraSecondary.500}, transparent 84%)',
                    focusBackground: 'color-mix(in srgb, {giraSecondary.500}, transparent 76%)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)',
                },
                mask: {
                    background: 'rgba(0,0,0,0.6)',
                    color: '{surface.200}',
                },
                formField: {
                    background: '{surface.900}',
                    disabledBackground: '{surface.700}',
                    filledBackground: '{surface.800}',
                    filledHoverBackground: '{surface.800}',
                    filledFocusBackground: '{surface.800}',
                    borderColor: '{surface.600}',
                    hoverBorderColor: '{surface.500}',
                    focusBorderColor: '{giraSecondary.300}',
                    invalidBorderColor: '{red.300}',
                    color: '{surface.50}',
                    disabledColor: '{surface.400}',
                    placeholderColor: '{surface.400}',
                    invalidPlaceholderColor: '{red.400}',
                    floatLabelColor: '{surface.400}',
                    floatLabelFocusColor: '{giraSecondary.300}',
                    floatLabelActiveColor: '{surface.400}',
                    floatLabelInvalidColor: '{red.400}',
                    iconColor: '{surface.400}',
                    shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
                },
                text: {
                    color: '{surface.50}',
                    hoverColor: '{surface.50}',
                    mutedColor: '{surface.400}',
                    hoverMutedColor: '{surface.300}',
                },
                content: {
                    background: '{surface.800}',
                    hoverBackground: '{surface.800}',
                    borderColor: '{surface.700}',
                    color: '{text.color}',
                    hoverColor: '{text.hover.color}',
                },
                overlay: {
                    select: {
                        background: '{surface.900}',
                        borderColor: '{surface.700}',
                        color: '{text.color}',
                    },
                    popover: {
                        background: '{surface.900}',
                        borderColor: '{surface.700}',
                        color: '{text.color}',
                    },
                    modal: {
                        background: '{surface.900}',
                        borderColor: '{surface.700}',
                        color: '{text.color}',
                    },
                },
                list: {
                    option: {
                        focusBackground: '{surface.800}',
                        selectedBackground: '{highlight.background}',
                        selectedFocusBackground: '{highlight.focus.background}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        selectedColor: '{highlight.color}',
                        selectedFocusColor: '{highlight.focus.color}',
                        icon: {
                            color: '{surface.500}',
                            focusColor: '{surface.400}',
                        },
                    },
                    optionGroup: {
                        background: 'transparent',
                        color: '{text.muted.color}',
                    },
                },
                navigation: {
                    item: {
                        focusBackground: '{surface.800}',
                        activeBackground: '{surface.800}',
                        color: '{text.color}',
                        focusColor: '{text.hover.color}',
                        activeColor: '{text.hover.color}',
                        icon: {
                            color: '{surface.500}',
                            focusColor: '{surface.400}',
                            activeColor: '{surface.400}',
                        },
                    },
                    submenuLabel: {
                        background: 'transparent',
                        color: '{text.muted.color}',
                    },
                    submenuIcon: {
                        color: '{surface.500}',
                        focusColor: '{surface.400}',
                        activeColor: '{surface.400}',
                    },
                },
            },
        },
    },
    components: {
        dialog: {
            css: `
.p-dialog-header,
.p-dialog-content,
.p-dialog-footer {
    transition: background-color var(--p-transition-duration), border-color var(--p-transition-duration), color var(--p-transition-duration);
}

.dark .p-dialog-header,
.dark .p-dialog-content,
.dark .p-dialog-footer {
    background: var(--p-overlay-modal-background)!important;
    color: var(--p-overlay-modal-color)!important;
    border-color: var(--p-overlay-modal-border-color)!important;
}

.dark .p-dialog-title,
.dark .p-dialog-header-icon,
.dark .p-dialog-close-button {
    color: inherit;
}
`,
        },
        datatable: {
            root: {
                transitionDuration: '{transition.duration}',
            },
            header: {
                background: '{content.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
            },
            headerCell: {
                background: '{content.background}',
                hoverBackground: '{content.hover.background}',
                selectedBackground: '{highlight.background}',
                borderColor: '{datatable.border.color}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                selectedColor: '{highlight.color}',
                padding: '0.75rem 1rem',
            },
            row: {
                background: '{content.background}',
                hoverBackground: '{content.hover.background}',
                selectedBackground: '{highlight.background}',
                color: '{content.color}',
                hoverColor: '{content.hover.color}',
                selectedColor: '{highlight.color}',
            },
            bodyCell: {
                borderColor: '{datatable.border.color}',
                padding: '0.75rem 1rem',
            },
            paginatorBottom: {
                borderColor: '{datatable.border.color}',
            },
            colorScheme: {
                light: {
                    root: {
                        borderColor: '{content.border.color}',
                    },
                    header: {
                        background: '{surface.100}',
                        borderColor: '{surface.200}',
                        color: '{giraPrimary.700}',
                    },
                    headerCell: {
                        background: '{surface.100}',
                        hoverBackground: '{surface.200}',
                        selectedBackground: '{giraPrimary.50}',
                        borderColor: '{surface.200}',
                        color: '{giraPrimary.700}',
                        hoverColor: '{giraPrimary.800}',
                        selectedColor: '{giraPrimary.700}',
                    },
                    row: {
                        stripedBackground: '{surface.50}',
                    },
                    bodyCell: {
                        selectedBorderColor: '{giraPrimary.100}',
                    },
                },
                dark: {
                    root: {
                        borderColor: '{surface.700}',
                    },
                    header: {
                        background: '{surface.9=800}',
                        borderColor: '{surface.700}',
                        color: '{surface.50}',
                    },
                    headerCell: {
                        background: '{surface.800}',
                        hoverBackground: '{surface.800}',
                        selectedBackground: 'color-mix(in srgb, {giraSecondary.500}, transparent 84%)',
                        borderColor: '{surface.700}',
                        color: '{surface.50}',
                        hoverColor: '{surface.0}',
                        selectedColor: '{surface.0}',
                    },
                    row: {
                        stripedBackground: '{surface.950}',
                    },
                    bodyCell: {
                        selectedBorderColor: '{giraSecondary.900}',
                    },
                },
            },
            css: `
.p-datatable-header,
.p-datatable-header-cell {
    transition: background-color var(--p-transition-duration), border-color var(--p-transition-duration), color var(--p-transition-duration);
}

.dark .p-datatable-header {
    background: var(--p-datatable-header-background)!important;
    color: var(--p-datatable-header-color)!important;
    border-color: var(--p-datatable-header-border-color)!important;
}

.dark .p-datatable-header-cell {
    background: var(--p-datatable-header-cell-background)!important;
    color: var(--p-datatable-header-cell-color)!important;
    border-color: var(--p-datatable-header-cell-border-color)!important;
}

.dark .p-datatable-column-title,
.dark .p-datatable-sort-icon {
    color: inherit;
}
`,
        },
    },
});

export default GiraBrasilTheme;
